/**
 * ============================================================================
 *  File        : frontend/src/api/client.ts
 *  Module      : api
 *  Purpose     : React API client — generated OpenAPI schema import + envelope helpers
 *  Author      : Phoenix Lai
 *  Created     : 2026-05-30
 *  Updated     : 2026-05-30
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 */

import { $fetch, type FetchOptions } from 'ofetch'
import type { components } from './generated/schema'
import type { ApiRow } from '../types'

export const API_BASE = '/api/v1'

export type ApiSchemas = components['schemas']

export interface ApiErrorBody {
  error: {
    code: string
    message_key: string
    details?: unknown
  }
}

export class ApiError extends Error {
  readonly status: number
  readonly body: ApiErrorBody | undefined

  constructor(status: number, body: ApiErrorBody | undefined, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

export interface Meta {
  page?: number
  per_page?: number
  total?: number
}

export interface Envelope<T> {
  data: T
  meta?: Meta
}

type TokenGetter = () => string | null
type UnauthorizedHandler = () => void

let tokenGetter: TokenGetter = () => null
let onUnauthorized: UnauthorizedHandler = () => undefined

export function configureApi(opts: {
  getToken: TokenGetter
  onUnauthorized: UnauthorizedHandler
}): void {
  tokenGetter = opts.getToken
  onUnauthorized = opts.onUnauthorized
}

export async function request<T>(url: string, opts: FetchOptions<'json'> = {}): Promise<T> {
  const token = tokenGetter()
  const headers = new Headers(opts.headers)
  headers.set('Accept', 'application/json')
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  try {
    return await $fetch<T>(url, { ...opts, headers })
  } catch (error) {
    const status = readNumber(error, 'status') ?? 0
    const body = readApiErrorBody(error)
    if (status === 401) {
      onUnauthorized()
    }
    throw new ApiError(status, body, body?.error.code ?? `HTTP ${status}`)
  }
}

export async function requestData<T>(url: string, opts: FetchOptions<'json'> = {}): Promise<T> {
  const envelope = await request<Envelope<T>>(url, opts)
  return envelope.data
}

export function requestEnvelope<T>(
  url: string,
  opts: FetchOptions<'json'> = {},
): Promise<Envelope<T>> {
  return request<Envelope<T>>(url, opts)
}

/** 高風險端點 2FA step-up code 攜帶的 header 名稱（spec §9.2，對齊後端）。 */
export const STEP_UP_HEADER = 'X-2FA-Code'

/**
 * 寫入 endpoint 共用 headers：每次新的 Idempotency-Key。
 * 若提供 `stepUpCode`（高風險端點需要的 2FA 一次性 code），附上 `X-2FA-Code`。
 */
export function mutationHeaders(stepUpCode?: string): Record<string, string> {
  const headers: Record<string, string> = { 'Idempotency-Key': crypto.randomUUID() }
  if (stepUpCode && stepUpCode.trim() !== '') {
    headers[STEP_UP_HEADER] = stepUpCode.trim()
  }
  return headers
}

export const api = {
  login(input: { email: string; password: string }): Promise<unknown> {
    return requestData<unknown>(`${API_BASE}/auth/login`, { method: 'POST', body: input })
  },
  me(): Promise<unknown> {
    return requestData<unknown>(`${API_BASE}/me`, { method: 'GET' })
  },
  logout(): Promise<unknown> {
    return requestData<unknown>(`${API_BASE}/auth/logout`, { method: 'POST' })
  },
  listRows(path: string): Promise<Envelope<ApiRow[]>> {
    return requestEnvelope<ApiRow[]>(path, { method: 'GET' })
  },
  /** 建立資料；高風險端點（master / admin）需帶 `stepUpCode`（→ X-2FA-Code）。 */
  createRow(path: string, body: ApiRow, stepUpCode?: string): Promise<ApiRow> {
    return requestData<ApiRow>(path, { method: 'POST', body, headers: mutationHeaders(stepUpCode) })
  },
  /** 更新資料；高風險端點（master / admin）需帶 `stepUpCode`（→ X-2FA-Code）。 */
  updateRow(path: string, body: ApiRow, stepUpCode?: string): Promise<ApiRow> {
    return requestData<ApiRow>(path, { method: 'PATCH', body, headers: mutationHeaders(stepUpCode) })
  },
  /** 刪除資料；高風險端點需帶 `stepUpCode`（→ X-2FA-Code）。 */
  deleteRow(path: string, stepUpCode?: string): Promise<ApiRow> {
    return requestData<ApiRow>(path, { method: 'DELETE', headers: mutationHeaders(stepUpCode) })
  },
}

function readNumber(value: unknown, key: string): number | undefined {
  if (typeof value !== 'object' || value === null || !(key in value)) return undefined
  const maybe = (value as Record<string, unknown>)[key]
  return typeof maybe === 'number' ? maybe : undefined
}

function readApiErrorBody(value: unknown): ApiErrorBody | undefined {
  if (typeof value !== 'object' || value === null || !('data' in value)) return undefined
  const data = (value as Record<string, unknown>)['data']
  if (typeof data !== 'object' || data === null || !('error' in data)) return undefined
  const error = (data as Record<string, unknown>)['error']
  if (typeof error !== 'object' || error === null) return undefined
  const code = (error as Record<string, unknown>)['code']
  const messageKey = (error as Record<string, unknown>)['message_key']
  if (typeof code !== 'string' || typeof messageKey !== 'string') return undefined
  return {
    error: {
      code,
      message_key: messageKey,
      details: (error as Record<string, unknown>)['details'],
    },
  }
}
